import {Component, Property} from '@wonderlandengine/api';

/**
 * Light Fade Controller - Controls smooth fading of lights
 *
 * Features:
 * - Smooth fade in/out for light intensity
 * - Can be marked as "fadeable" or "spotlight"
 * - Global coordination with InteractiveObject components
 * - Initial fade-in on experience start
 *
 * Setup:
 * 1. Add to any object with a light component
 * 2. Set lightType to control behavior:
 *    - "fadeable": Will fade out when any interactive object triggers (default)
 *    - "spotlight": Will only activate for its assigned interactive object
 *    - "static": Never fades (always on)
 * 3. Configure fade durations and initial state
 */
export class LightFadeController extends Component {
    static TypeName = 'light-fade-controller';
    static Properties = {
        lightType: Property.enum(['fadeable', 'spotlight', 'static'], 'fadeable'),
        fadeInDuration: Property.float(3.0),
        fadeOutDuration: Property.float(2.0),
        initialIntensity: Property.float(1.0),
        startFadedOut: Property.bool(true),  // Start with lights off
        initialFadeInDelay: Property.float(1.0),  // Delay before initial fade-in
        debugMode: Property.bool(true)
    };

    init() {
        // Light component reference
        this.lightComponent = null;

        // Fade state
        this.isFading = false;
        this.currentIntensity = this.startFadedOut ? 0 : this.initialIntensity;
        this.targetIntensity = this.initialIntensity;
        this.fadeStartIntensity = this.currentIntensity;
        this.fadeInterval = null;
        this.fadeCallback = null;

        // Store original intensity
        this.originalIntensity = this.initialIntensity;

        // Register globally for coordination
        if (!window.lightControllers) {
            window.lightControllers = [];
        }
        window.lightControllers.push(this);

        console.log('[LightFadeController] Initialized');
    }

    start() {
        // Get light component
        this.lightComponent = this.object.getComponent('light');
        if (!this.lightComponent) {
            console.error('[LightFadeController] No light component found on object!');
            return;
        }

        // Store the original intensity from the light component itself
        // This respects what was set in the editor
        this.editorIntensity = this.lightComponent.intensity;

        // For spotlights, use editor intensity as the target, otherwise use initialIntensity
        if (this.lightType === 1) { // 'spotlight'
            this.originalIntensity = this.editorIntensity > 0 ? this.editorIntensity : this.initialIntensity;
        } else {
            this.originalIntensity = this.initialIntensity;
        }

        // Set initial intensity based on type
        if (this.lightType === 1) { // 'spotlight' - always start off
            this.lightComponent.intensity = 0;
            this.currentIntensity = 0;
            console.log('[LightFadeController] Spotlight starting OFF, will fade to:', this.originalIntensity);
        } else if (this.lightType === 2) { // 'static' - always on, never fades
            // Static lights should always be on at their set intensity
            this.lightComponent.intensity = this.originalIntensity;
            this.currentIntensity = this.originalIntensity;
            console.log('[LightFadeController] Static light always ON at:', this.originalIntensity);
        } else if (this.lightType === 0) { // 'fadeable'
            if (this.startFadedOut) {
                // Fadeable lights that start faded out
                this.lightComponent.intensity = 0;
                this.currentIntensity = 0;

                // Schedule initial fade-in
                setTimeout(() => {
                    console.log('[LightFadeController] Starting initial fade-in for fadeable light');
                    this.fadeIn();
                }, this.initialFadeInDelay * 1000);
            } else {
                // Fadeable lights that don't start faded out
                this.lightComponent.intensity = this.originalIntensity;
                this.currentIntensity = this.originalIntensity;
            }
        }

        if (this.debugMode) {
            console.log('[LightFadeController] Started:', {
                type: ['fadeable', 'spotlight', 'static'][this.lightType],
                editorIntensity: this.editorIntensity,
                targetIntensity: this.originalIntensity,
                currentIntensity: this.currentIntensity,
                startFadedOut: this.startFadedOut,
                object: this.object.name
            });
        }
    }

    /**
     * Fade out the light smoothly
     * @param duration Optional custom fade duration
     * @param onComplete Optional callback when fade completes
     */
    fadeOut(duration = null, onComplete = null) {
        // Static lights don't fade
        if (this.lightType === 2) return; // 'static'

        const fadeDuration = duration !== null ? duration : this.fadeOutDuration;

        if (this.debugMode) {
            console.log(`[LightFadeController] Fading out over ${fadeDuration}s`);
        }

        this.startFade(0, fadeDuration, onComplete);
    }

    /**
     * Fade in the light smoothly
     * @param duration Optional custom fade duration
     * @param targetIntensity Optional target intensity (uses original by default)
     * @param onComplete Optional callback when fade completes
     */
    fadeIn(duration = null, targetIntensity = null, onComplete = null) {
        const fadeDuration = duration !== null ? duration : this.fadeInDuration;
        const target = targetIntensity !== null ? targetIntensity : this.originalIntensity;

        if (this.debugMode) {
            console.log(`[LightFadeController] fadeIn called:`, {
                currentIntensity: this.currentIntensity,
                targetIntensity: target,
                originalIntensity: this.originalIntensity,
                editorIntensity: this.editorIntensity,
                duration: fadeDuration,
                lightType: ['fadeable', 'spotlight', 'static'][this.lightType],
                objectName: this.object.name
            });
        }

        this.startFade(target, fadeDuration, onComplete);
    }

    /**
     * Fade to a specific intensity
     * @param targetIntensity Target intensity
     * @param duration Fade duration in seconds
     * @param onComplete Optional callback when fade completes
     */
    fadeTo(targetIntensity, duration, onComplete = null) {
        if (this.debugMode) {
            console.log(`[LightFadeController] Fading to ${targetIntensity} over ${duration}s`);
        }

        this.startFade(targetIntensity, duration, onComplete);
    }

    startFade(targetIntensity, duration, onComplete) {
        // Clear any existing fade
        if (this.fadeInterval) {
            clearInterval(this.fadeInterval);
        }

        // Set fade parameters
        this.fadeStartIntensity = this.currentIntensity;
        this.targetIntensity = targetIntensity;
        this.fadeCallback = onComplete;
        this.isFading = true;

        // Calculate fade steps
        const steps = 30; // 30 updates per second
        const stepDuration = 1000 / steps;
        const totalSteps = Math.floor(duration * steps);
        let currentStep = 0;

        this.fadeInterval = setInterval(() => {
            currentStep++;
            const progress = currentStep / totalSteps;

            // Use easing for smooth perception
            const easedProgress = this.easeInOutQuad(progress);

            // Calculate current intensity
            this.currentIntensity = this.fadeStartIntensity +
                (this.targetIntensity - this.fadeStartIntensity) * easedProgress;

            // Apply to light component
            if (this.lightComponent) {
                this.lightComponent.intensity = this.currentIntensity;
            }

            if (this.debugMode && currentStep % steps === 0) {
                console.log(`[LightFadeController] Fade progress: ${Math.round(progress * 100)}%, intensity: ${this.currentIntensity.toFixed(3)}`);
            }

            // Check if fade is complete
            if (currentStep >= totalSteps) {
                clearInterval(this.fadeInterval);
                this.fadeInterval = null;
                this.isFading = false;

                // Ensure we're at target intensity
                this.currentIntensity = this.targetIntensity;
                if (this.lightComponent) {
                    this.lightComponent.intensity = this.targetIntensity;
                }

                if (this.debugMode) {
                    console.log('[LightFadeController] Fade complete');
                }

                if (this.fadeCallback) {
                    this.fadeCallback();
                    this.fadeCallback = null;
                }
            }
        }, stepDuration);
    }

    /**
     * Stop any ongoing fade
     */
    stopFade() {
        if (this.fadeInterval) {
            clearInterval(this.fadeInterval);
            this.fadeInterval = null;
            this.isFading = false;
        }
    }

    /**
     * Set intensity immediately without fade
     * @param intensity New intensity value
     */
    setIntensityImmediate(intensity) {
        this.currentIntensity = intensity;
        this.targetIntensity = intensity;
        if (this.lightComponent) {
            this.lightComponent.intensity = intensity;
        }
        this.stopFade();
    }

    // Easing function for smooth perception
    easeInOutQuad(t) {
        return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    }

    onDestroy() {
        // Clear any running fade
        if (this.fadeInterval) {
            clearInterval(this.fadeInterval);
        }

        // Remove from global registry
        if (window.lightControllers) {
            const index = window.lightControllers.indexOf(this);
            if (index > -1) {
                window.lightControllers.splice(index, 1);
            }
        }
    }

    // Public API for InteractiveObject coordination

    /**
     * Check if this light should fade for a given interactive object
     */
    shouldFadeForObject(interactiveObject) {
        // Fadeable lights always fade
        if (this.lightType === 0) return true; // 'fadeable'

        // Static lights never fade
        if (this.lightType === 2) return false; // 'static'

        // Spotlight only stays on for its assigned object
        // This requires InteractiveObject to have a spotlight property
        return false;
    }

    /**
     * Check if this is a spotlight for a given interactive object
     */
    isSpotlightForObject(interactiveObject) {
        if (this.lightType !== 1) {
            return false; // Not a spotlight
        }

        // Check if interactive object has a spotlight assigned
        const hasSpotlight = interactiveObject.spotlightObject !== null &&
                            interactiveObject.spotlightObject !== undefined;

        if (!hasSpotlight) {
            if (this.debugMode) {
                console.log('[LightFadeController] Interactive object has NO spotlight assigned');
            }
            return false;
        }

        // Compare the objects
        const areEqual = interactiveObject.spotlightObject === this.object;

        // Debug logging to understand the comparison
        if (this.debugMode) {
            console.log('[LightFadeController] Spotlight check:', {
                thisLightName: this.object.name,
                assignedSpotlightName: interactiveObject.spotlightObject.name,
                areTheSameObject: areEqual,
                thisObjectId: this.object.id,
                spotlightObjectId: interactiveObject.spotlightObject.id
            });
        }

        return areEqual;
    }
}